import React, { useMemo, useState } from 'react';
import { Check, Mail } from 'lucide-react';
import EmailQuoteDialog from './EmailQuoteDialog';

/**
 * Self-Publishing pricing estimator.
 *
 * Every book is different, so we don't pretend to give a fixed quote —
 * this returns an indicative "from" ballpark based on word count and the
 * scope items the author actually wants us to run. Rates signed off by Jo
 * (Feb 2026):
 *   - Book formatting: £7.50 per 1,000 words
 *   - Copy edit:       £7.50 per 1,000 words
 *   - Proof read:      £7.50 per 1,000 words
 *   - Cover design:    £195 one-off
 *   - KDP setup:       £75 one-off
 *   - Hardcover add-on / launch: fixed one-off fees.
 *
 * The word-count multiplier is per-1,000-words for word-count-sensitive
 * items (copy edit, proof read, formatting).
 */
const SCOPE_ITEMS = [
  { id: 'formatting',   label: 'Book formatting',                     desc: 'Paperback + Kindle interior files', base: 0,   perK: 7.5, wordSensitive: true },
  { id: 'hardcover',    label: 'Add hardcover format',                desc: 'Separate hardcover interior + cover extension', base: 120, perK: 0,   wordSensitive: false, requires: 'formatting' },
  { id: 'copyEdit',     label: 'Copy edit',                           desc: 'Line-by-line edit for clarity and voice',       base: 0,   perK: 7.5, wordSensitive: true },
  { id: 'proofRead',    label: 'Proof read',                          desc: 'Final catch-all pass before print',             base: 0,   perK: 7.5, wordSensitive: true },
  { id: 'cover',        label: 'Cover design',                        desc: 'Three concepts, one final, front+spine+back',   base: 195, perK: 0,   wordSensitive: false },
  { id: 'amazonSetup',  label: 'Amazon KDP setup + metadata',         desc: 'Account setup, categories, keywords, blurb',    base: 75,  perK: 0,   wordSensitive: false },
  { id: 'launch',       label: 'Launch week campaign',                desc: 'Launch page, Kindle deal, reviews outreach',    base: 380, perK: 0,   wordSensitive: false },
];

const DEFAULT_SELECTED = ['formatting'];

// One-tap starting points. Each preset sets a realistic word count and
// preselects the scope items authors of that book type almost always ask
// for. Users can still tweak everything after tapping.
const PRESETS = [
  {
    id: 'memoir',
    label: 'Memoir',
    hint: '60k words, full done-for-you',
    wordCount: 60_000,
    scope: ['formatting', 'copyEdit', 'proofRead', 'cover', 'amazonSetup'],
  },
  {
    id: 'business-book',
    label: 'Business book',
    hint: '25k words, punchy + launch',
    wordCount: 25_000,
    scope: ['formatting', 'copyEdit', 'proofRead', 'cover', 'amazonSetup', 'launch'],
  },
  {
    id: 'novel',
    label: 'Full novel',
    hint: '80k words, the lot',
    wordCount: 80_000,
    scope: ['formatting', 'hardcover', 'copyEdit', 'proofRead', 'cover', 'amazonSetup', 'launch'],
  },
];

// Add a ~25% band on top so the user sees a range not a single figure.
const RANGE_MULTIPLIER = 1.25;

function gbp(n) {
  return `£${Math.round(n).toLocaleString()}`;
}

export default function PricingEstimator({ testId = 'pricing-estimator' }) {
  const [wordCount, setWordCount] = useState(60_000);
  const [selected, setSelected] = useState(new Set(DEFAULT_SELECTED));
  const [activePreset, setActivePreset] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const applyPreset = (preset) => {
    setWordCount(preset.wordCount);
    setSelected(new Set(preset.scope));
    setActivePreset(preset.id);
  };

  const toggle = (id) => {
    const item = SCOPE_ITEMS.find((s) => s.id === id);
    setActivePreset(null); // manual tweak clears preset highlight
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (item?.requires) next.add(item.requires);
      }
      return next;
    });
  };

  const { low, high, breakdown } = useMemo(() => {
    const kWords = wordCount / 1000;
    let subtotal = 0;
    const rows = [];
    for (const item of SCOPE_ITEMS) {
      if (!selected.has(item.id)) continue;
      const cost = item.base + (item.wordSensitive ? item.perK * kWords : 0);
      subtotal += cost;
      rows.push({ id: item.id, label: item.label, cost });
    }
    return {
      low: subtotal,
      high: subtotal * RANGE_MULTIPLIER,
      breakdown: rows,
    };
  }, [wordCount, selected]);

  return (
    <div
      className="bg-white rounded-[16px] border border-[#E2E8F0] p-6 sm:p-8 shadow-[0_18px_40px_-24px_rgba(30,42,68,0.35)]"
      data-testid={testId}
    >
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h3 className="font-display font-semibold text-[#1E2A44] text-xl sm:text-2xl">
          Estimate your book
        </h3>
        <p className="text-xs uppercase tracking-widest text-[#94A3B8]">Indicative ballpark</p>
      </div>
      <p className="text-[#475569] mt-2 text-sm leading-relaxed">
        Every book is different, so this is a live ballpark, not a quote. Slide your word count, tick the pieces you want us to run, and see roughly where you'd land. Final number comes after we read a sample chapter on the free call.
      </p>

      {/* Presets — one-tap starting points */}
      <div className="mt-5" data-testid={`${testId}-presets`}>
        <p className="text-[#1E2A44] font-semibold text-sm mb-2">Start from a typical book</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => {
            const isActive = activePreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                aria-pressed={isActive}
                className={`text-left rounded-[10px] border px-3.5 py-2.5 transition-all ${
                  isActive
                    ? 'border-[#BC516F] bg-[#BC516F] text-white shadow-[0_6px_16px_-6px_rgba(188,81,111,0.55)]'
                    : 'border-[#E2E8F0] bg-white text-[#1E2A44] hover:border-[#BC516F] hover:bg-[#FFF3F6]'
                }`}
                data-testid={`${testId}-preset-${preset.id}`}
              >
                <div className="font-semibold text-sm leading-tight">{preset.label}</div>
                <div className={`text-xs mt-0.5 ${isActive ? 'text-white/85' : 'text-[#64748B]'}`}>{preset.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Word count */}
      <div className="mt-6">
        <div className="flex justify-between items-baseline">
          <label htmlFor="wordCount" className="text-[#1E2A44] font-semibold text-sm">
            Word count
          </label>
          <span className="font-display text-[#BC516F] font-bold text-lg" data-testid={`${testId}-word-count`}>
            {wordCount.toLocaleString()} words
          </span>
        </div>
        <input
          id="wordCount"
          type="range"
          min="20000"
          max="150000"
          step="5000"
          value={wordCount}
          onChange={(e) => { setWordCount(parseInt(e.target.value, 10)); setActivePreset(null); }}
          className="w-full mt-2 accent-[#BC516F]"
          data-testid={`${testId}-word-range`}
        />
        <div className="flex justify-between text-xs text-[#94A3B8] mt-1">
          <span>20k</span><span>60k (avg memoir)</span><span>90k (novel)</span><span>150k</span>
        </div>
      </div>

      {/* Scope checkboxes */}
      <div className="mt-6">
        <p className="text-[#1E2A44] font-semibold text-sm">What do you want us to run?</p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {SCOPE_ITEMS.map((item) => {
            const active = selected.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => toggle(item.id)}
                aria-pressed={active}
                className={`text-left rounded-[10px] border p-3 transition-all ${
                  active
                    ? 'border-[#BC516F] bg-[#FFF3F6]'
                    : 'border-[#E2E8F0] bg-white hover:border-[#94A3B8]'
                }`}
                data-testid={`${testId}-scope-${item.id}`}
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className={`mt-0.5 w-5 h-5 rounded-[6px] flex items-center justify-center flex-shrink-0 ${
                      active ? 'bg-[#BC516F] text-white' : 'bg-[#F1F5F9]'
                    }`}
                  >
                    {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1E2A44] text-sm leading-tight">{item.label}</p>
                    <p className="text-[#475569] text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Result */}
      <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-[#94A3B8]">Indicative ballpark</p>
          {breakdown.length === 0 ? (
            <p className="font-display font-bold text-[#1E2A44] text-2xl mt-1">
              Pick at least one option
            </p>
          ) : (
            <p className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl mt-1" data-testid={`${testId}-total`}>
              {gbp(low)} <span className="text-[#94A3B8] font-normal text-xl">–</span> {gbp(high)}
            </p>
          )}
          <p className="text-[#475569] text-xs mt-1">
            One-off. Quoted properly on the free call.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            type="button"
            onClick={() => setEmailOpen(true)}
            disabled={breakdown.length === 0}
            className="inline-flex items-center justify-center gap-2 border-2 border-[#BC516F] text-[#BC516F] hover:bg-[#FFF3F6] disabled:opacity-40 disabled:cursor-not-allowed font-semibold px-4 py-3 rounded-[10px] transition-colors text-sm"
            data-testid={`${testId}-email-me`}
          >
            <Mail className="w-4 h-4" /> Email me this quote
          </button>
          <a
            href="https://calendly.com/audioandco/30min"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-[#BC516F] hover:bg-[#A7468A] text-white font-semibold px-5 py-3 rounded-[10px] transition-colors text-sm"
            data-testid={`${testId}-cta`}
          >
            Get an exact quote
          </a>
        </div>
      </div>

      <EmailQuoteDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        testId={`${testId}-email`}
        getPayload={() => ({
          service: 'self-publishing',
          total_label: `${gbp(low)} – ${gbp(high)} one-off`,
          line_items: [
            { label: 'Word count', detail: `${wordCount.toLocaleString()} words`, amount: '' },
            ...breakdown.map((row) => ({
              label: row.label,
              detail: '',
              amount: gbp(row.cost),
            })),
          ],
          notes: 'Formatting, copy edit and proof read are billed at £7.50 per 1,000 words. Cover £195, KDP setup £75.',
        })}
      />
    </div>
  );
}
